import { Grid } from '@mui/material';
import { Checkbox, RoutalPalette } from '@nx-smartmonkey/ui';

import { StopCanceledManagementIcon } from '../../assets/svg/StopCanceledManagementIcon';
import { StopCompleteManagementIcon } from '../../assets/svg/StopCompleteManagementIcon';
import { StopIncompleteManagementIcon } from '../../assets/svg/StopIncompleteManagementIcon';
import { StopPendingManagementIcon } from '../../assets/svg/StopPendingManagementIcon';
import { StopStatus } from '../../constants/Stops';
import { Stop } from '../../domain/stops/Stop';
import { LabelStop, TagContainer } from '../../pages/app/route/stops/StopPill';
import { Text } from '../../react-components/Text';
import { StopManagementMenuTagText } from './StopManagementMenuTagText';

interface SelectorStopsManagementMenuProps {
  type: StopStatus;
  stops?: Stop[];
  selectedStopsIds: string[];
  setSelectedStopsIds: (selectedStopsIds: string[]) => void;
  disabled?: boolean;
}

export const SelectorStopsManagementMenu = ({
  type,
  stops,
  selectedStopsIds,
  setSelectedStopsIds,
  disabled,
}: SelectorStopsManagementMenuProps) => {
  if (!stops || stops.length === 0) return null;
  const stopsIds = stops.map((stop) => stop.id);

  const getIconFromType = (type: StopStatus) => {
    switch (type) {
      case `pending`:
        return <StopPendingManagementIcon style={{ cursor: `pointer`, height: `22px`, width: `22px` }} />;
      case `completed`:
        return <StopCompleteManagementIcon style={{ cursor: `pointer`, height: `22px`, width: `22px` }} />;
      case `canceled`:
        return <StopCanceledManagementIcon style={{ cursor: `pointer`, height: `22px`, width: `22px` }} />;
      default:
        return <StopIncompleteManagementIcon style={{ cursor: `pointer`, height: `22px`, width: `22px` }} />;
    }
  };

  let colors;
  if (type === `pending`) {
    colors = {
      mainColor: RoutalPalette.primary20n,
      secondaryColor: RoutalPalette.primary40,
      textColor: RoutalPalette.primary40,
    };
  } else if (type === `completed`) {
    colors = {
      mainColor: RoutalPalette.stopStatusColors.completed.dark,
      secondaryColor: RoutalPalette.stopStatusColors.completed.dark,
      textColor: RoutalPalette.neutral02,
    };
  } else if (type === `canceled`) {
    colors = {
      mainColor: RoutalPalette.stopStatusColors.canceled.dark,
      secondaryColor: RoutalPalette.stopStatusColors.canceled.dark,
      textColor: RoutalPalette.neutral02,
    };
  } else {
    colors = {
      mainColor: RoutalPalette.stopStatusColors.incomplete.dark,
      secondaryColor: RoutalPalette.stopStatusColors.incomplete.dark,
      textColor: RoutalPalette.neutral02,
    };
  }

  const manageSelectStop = (stopId: string) => {
    if (selectedStopsIds.includes(stopId)) {
      setSelectedStopsIds(selectedStopsIds.filter((id) => id !== stopId));
    } else {
      setSelectedStopsIds([...selectedStopsIds, stopId]);
    }
  };

  const checked = stops.length > 0 && stops.every((stop) => selectedStopsIds?.includes(stop.id));
  const onToggle = () => {
    if (!disabled) {
      if (stops.length > 0 && checked) {
        setSelectedStopsIds(selectedStopsIds.filter((id) => !stopsIds.includes(id)));
      } else {
        setSelectedStopsIds([...selectedStopsIds, ...stops.map((stop) => stop.id)]);
      }
    }
  };

  const isIndeterminate = () => {
    const selectedStopsFromList = stops.filter((stop) => selectedStopsIds.includes(stop.id));
    return selectedStopsFromList.length > 0 && selectedStopsFromList.length < stops.length;
  };

  return (
    <Grid container>
      <Grid
        container
        direction="row"
        wrap="nowrap"
        height="64px"
        alignItems="center"
        style={{
          padding: `0 20px`,
          background: RoutalPalette.neutral02,
        }}
      >
        <Grid container direction="row" wrap="nowrap" alignItems="center" gap="6px">
          {getIconFromType(type)}
          <Text variant="body-large" intlId={`driver.route.stops.config.${type}`} />
        </Grid>

        <Checkbox
          size="medium"
          indeterminate={isIndeterminate()}
          checked={checked}
          onChange={onToggle}
          disabled={disabled}
        />
      </Grid>
      <Grid
        container
        direction="column"
        wrap="nowrap"
        alignItems="center"
        style={{
          minHeight: `80px`,
          justifyContent: `center`,
          backgroundColor: RoutalPalette.neutral05,
        }}
      >
        {stops.length > 0 ? (
          stops.map((stop, index) => (
            <Grid
              key={stop.id}
              container
              direction="row"
              wrap="nowrap"
              alignItems="center"
              style={{
                padding: `20px`,
                borderBottom: index === stops.length - 1 ? `none` : `1px solid ${RoutalPalette.neutral10}`,
                backgroundColor: selectedStopsIds?.includes(stop.id) ? RoutalPalette.neutral00 : RoutalPalette.white,
              }}
              onClick={() => {
                if (!disabled) manageSelectStop(stop.id);
              }}
            >
              <TagContainer tagStyle="unhighlighted" height="24px" width="24px">
                <StopManagementMenuTagText stopType={stop.type} {...colors} height="24px" width="24px">
                  {stop.order + 1}
                </StopManagementMenuTagText>
              </TagContainer>

              <Grid container direction="column" style={{ margin: `0 12px` }}>
                <LabelStop
                  labelStyles={{
                    color: RoutalPalette.primary80,
                    height: `fit-content`,
                  }}
                >
                  {stop.location?.label}
                </LabelStop>
                {stop.label ? (
                  <LabelStop
                    labelStyles={{ color: RoutalPalette.secondary00, fontSize: `13px`, height: `fit-content` }}
                  >
                    {stop.label}
                  </LabelStop>
                ) : null}
              </Grid>

              <Grid>
                <Checkbox
                  size="medium"
                  checked={selectedStopsIds.includes(stop.id)}
                  onChange={() => {
                    if (!disabled) manageSelectStop(stop.id);
                  }}
                  disabled={disabled}
                />
              </Grid>
            </Grid>
          ))
        ) : (
          <Grid>
            <Text variant="label" intlId={`driver.stops.management.selector.no_stops.${type}`} />
          </Grid>
        )}
      </Grid>
    </Grid>
  );
};
